import React, {ReactElement} from 'react';
import {useTranslation} from 'react-i18next';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import {IElementQuestionYesNo} from '../../../../../incase-domain/elements/question/questionYesNo';
import NumberInput from '../../../../../ui/components/NumberInput';
import {generateErrorMessages, getErrorClasses} from '../../../../../utils/validation';
import BorderRadiusChange from '../../common/borderRadiusChange';
import ElementShadow from '../../common/elementShadow';
import FontSelect from '../../common/fontSelect';
import {TABS} from '../../../form/part/tabs';
import ChecklistTile from '../../../../../incase-domain/components/checkbox/ChecklistTile';
import SectionTitle from '../../../../elements/common/SectionTitle';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';
import DebouncedNumberInput from '../../common/debounced/DebounceNumberInput';
import {MAX_QUESTION_LENGTH} from '../../../../../incase-domain/validators/elements/constant';
import ColorSelectCompact from '../../../../../incase-domain/components/select/compact/color/colorSelectCompact/ColorSelectCompact';
import YesNoAnswerGroup from './YesNoAnswerGroup';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedYesNoQuestionElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

/**
 * @deprecated This component is deprecated. Please use the new HighlightedQuestionYesNoV2ElementComponent instead.
 */
function HighlightedYesNoQuestionElementComponent(props: HighlightedYesNoQuestionElementComponentProps): ReactElement {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();
    const highlightedElement = useHighlightedElement() as IElementQuestionYesNo;

    const commentColor = highlightedElement.commentColor || '#000000';
    const backgroundColor = highlightedElement.backgroundColor || '#000000';

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.wrapper')} />
                    <DebouncedNumberInput
                        label={t('designSidepanel.fontSize')}
                        value={highlightedElement.questionFontSize || 1}
                        onChange={(newValue: number) => updateHighlightedElement.single('questionFontSize', newValue)}
                        step={0.1}
                        min={0.1}
                    />
                    <div className="l-group__item">
                        <FontSelect highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
                    </div>
                    <div className="l-group__item">
                        <ColorSelectCompact
                            label={t('designSidepanel.backgroundColor')}
                            color={highlightedElement.questionBackgroundColor || ''}
                            onChange={(e) => {
                                updateHighlightedElement.single('questionBackgroundColor', e);
                            }}
                        />
                    </div>
                </div>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.comment')} />
                    <div className="u-flex  u-flex-col u-gap-2">
                        <DebouncedNumberInput
                            label={t('designSidepanel.fontSize')}
                            step={0.1}
                            min={0.1}
                            value={highlightedElement.commentFontSize || 1}
                            onChange={(newValue) => updateHighlightedElement.single('commentFontSize', newValue)}
                        />

                        <ColorSelectCompact
                            label={t('designSidepanel.textColor')}
                            color={commentColor}
                            onChange={(e) => {
                                updateHighlightedElement.single('commentColor', e);
                            }}
                        />

                        <ColorSelectCompact
                            label={t('designSidepanel.backgroundColor')}
                            color={backgroundColor}
                            onChange={(e) => {
                                updateHighlightedElement.single('backgroundColor', e);
                            }}
                        />
                        <div className="l-group__item">
                            <BorderRadiusChange
                                borderRadius={highlightedElement.borderRadius}
                                highlightedElementChangeHandler={(value) => updateHighlightedElement.single('borderRadius', value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.button')} />
                    <NumberInput
                        label={t('designSidepanel.fontSize')}
                        step={0.1}
                        min={0.1}
                        value={highlightedElement.fontSize || 1.2}
                        onChange={(newValue) => updateHighlightedElement.single('fontSize', newValue)}
                    />
                </div>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.preferences')} />
                    <div className="u-flex u-flex-col u-gap-2">
                        <ChecklistTile
                            value={!!highlightedElement.blockingQuestion}
                            label={t('designSidepanel.blockingQuestion')}
                            help={t('designSidepanel.blockingQuestionExpl')}
                            onChange={(value) => updateHighlightedElement.single('blockingQuestion', value)}
                        />
                        <ChecklistTile
                            value={!!highlightedElement.showInReport}
                            label={t('designSidepanel.showInReport')}
                            onChange={(value) => updateHighlightedElement.single('showInReport', value)}
                        />

                        <ChecklistTile
                            value={highlightedElement.showAllComments}
                            label={t('designSidepanel.showAllComments')}
                            help={t('designSidepanel.showAllCommentsExpl')}
                            onChange={(value) => updateHighlightedElement.single('showAllComments', value)}
                        />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <div className={`l-group__item ${getErrorClasses(errors, 'question')}`}>
                    <SectionTitle label={t('designSidepanel.question')} />
                    <div>
                        <DebouncedTextInput
                            className="c-sidepanel__input"
                            value={highlightedElement.question}
                            onChange={(value) => updateHighlightedElement.single('question', value)}
                            maxLength={MAX_QUESTION_LENGTH}
                        />
                    </div>
                    {generateErrorMessages(errors, 'question')}
                </div>
                <ChecklistTile
                    value={!!highlightedElement.isDebatable}
                    label={t('designSidepanel.debatableQuestion')}
                    onChange={(value) => updateHighlightedElement.multiple(['isDebatable', 'correct'], [value, 0])}
                />
            </div>

            <div className="l-group--form">
                <SectionTitle label={`${t('designSidepanel.answers')}`} />
                <YesNoAnswerGroup
                    index={1}
                    errors={errors}
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />

                <YesNoAnswerGroup
                    index={0}
                    errors={errors}
                    highlightedElement={highlightedElement}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </div>
        </>
    );
}

export default HighlightedYesNoQuestionElementComponent;
